import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Edit, Trash2, CalendarClock, Building2, User } from 'lucide-react';
import { formatDate } from '@/lib/interactions';

const API = `${window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || ''}/api`;

/** Temas y clubes para el formulario y los filtros. */
export const useInteractionLookups = () => {
  const [topics, setTopics] = useState([{ id: 'general', name: 'General' }]);
  const [clubs, setClubs] = useState([]);
  const reload = useCallback(async () => {
    try {
      const [t, c] = await Promise.all([axios.get(`${API}/interactions/topics`), axios.get(`${API}/interactions/clubs`)]);
      setTopics(t.data);
      setClubs(c.data);
    } catch (_) { /* se mantiene lo anterior */ }
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return { topics, clubs, reload };
};

export const InteractionCard = ({ entry, canEdit, showScout, onEdit, onDeleted }) => {
  const [confirm, setConfirm] = useState(false);

  const remove = async () => {
    try {
      await axios.delete(`${API}/interactions/${entry.id}`);
      toast.success('Entrada eliminada');
      setConfirm(false);
      onDeleted && onDeleted();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const who = [entry.counterpart_label, entry.contact_name, entry.contact_role].filter(Boolean).join(' · ');

  return (
    <Card className="p-4 space-y-2" data-testid={`interaction-${entry.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-sm">{formatDate(entry.date)}</span>
          <Badge variant="outline">{entry.kind_label}</Badge>
          <Badge className="bg-brand-100 text-brand-800 border border-brand-300">{entry.topic_name}</Badge>
          {showScout && <Badge variant="outline" className="text-slate-600">{entry.created_by_name}</Badge>}
          {entry.player_inactive && <Badge className="bg-gray-200 text-gray-700">Jugador desactivado</Badge>}
        </div>
        {canEdit && (
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" onClick={() => onEdit(entry)} title="Editar" data-testid={`edit-${entry.id}`}><Edit className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => setConfirm(true)} title="Eliminar" data-testid={`delete-${entry.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
        {who && <span>{who}</span>}
        {entry.player_id ? (
          <Link to={`/players/${entry.player_id}`} className="inline-flex items-center gap-1 text-brand-700 hover:underline"><User className="w-3.5 h-3.5" />{entry.player_name}</Link>
        ) : entry.about_name ? (
          <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" />{entry.about_name} <span className="text-xs text-slate-400">(sin ficha)</span></span>
        ) : entry.player_name ? (
          <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" />{entry.player_name} <span className="text-xs text-slate-400">(ficha eliminada)</span></span>
        ) : null}
        {entry.club && <span className="inline-flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{entry.club}</span>}
      </div>
      <p className="text-sm whitespace-pre-wrap" data-testid="interaction-text">{entry.text}</p>
      {(entry.next_step || entry.next_step_date) && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-flex items-center gap-1">
          <CalendarClock className="w-3.5 h-3.5" /> Próximo paso: {entry.next_step || '—'}{entry.next_step_date ? ` (${formatDate(entry.next_step_date)})` : ''}
        </p>
      )}
      {entry.edited && <p className="text-xs text-slate-400">Editada</p>}

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta entrada?</AlertDialogTitle>
            <AlertDialogDescription>Se borra la nota del {formatDate(entry.date)}. No se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-interaction">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
